import type { Id } from "@convex/_generated/dataModel";
import { cardColors } from "@plasma/data/card-colors";

import { toConvexTableId } from "~/lib/convex-table-id";
import { enumIncludes, parseEnumValue } from "~/lib/parse-enum-value";

const CARD_COLOR_KEYS = [
  "blue",
  "gray",
  "green",
  "purple",
  "red",
  "rose",
  "white",
] as const;
export const isCardColorKey = enumIncludes(CARD_COLOR_KEYS);
export { CARD_COLOR_KEYS };

export function getCardColorStyle(
  color: string
): (typeof cardColors)[keyof typeof cardColors] {
  const key = parseEnumValue(color, isCardColorKey, "blue");
  return cardColors[key];
}

const ROUTING_MODES = ["all", "categories", "items"] as const;
export const isRoutingMode = enumIncludes(ROUTING_MODES);

const FILTER_ROUTING_MODES = ["categories", "items"] as const;
export const isFilterRoutingMode = enumIncludes(FILTER_ROUTING_MODES);

export const AUTO_PRINT_VALUES = ["off", "onReceived", "onBump"] as const;
export const isAutoPrint = enumIncludes(AUTO_PRINT_VALUES);

export const PRINT_TYPE_VALUES = ["ticket", "label"] as const;
export const isPrintType = enumIncludes(PRINT_TYPE_VALUES);

export const SORT_MODE_VALUES = ["fifo", "rule-based", "hybrid"] as const;
export const isSortMode = enumIncludes(SORT_MODE_VALUES);

export const CROSS_SCREEN_MODE_VALUES = [
  "multi-clear",
  "send-to-next",
  "prep-expo",
  "independent",
] as const;
export const isCrossScreenMode = enumIncludes(CROSS_SCREEN_MODE_VALUES);

export const STATION_ROLE_VALUES = ["prep", "expo"] as const;
export const isStationRole = enumIncludes(STATION_ROLE_VALUES);

export const FONT_SIZE_VALUES = ["small", "medium", "large"] as const;
export const isFontSize = enumIncludes(FONT_SIZE_VALUES);

// Type derived from Convex query return -- using a permissive shape with known property types
export interface StationType {
  [key: string]: unknown;
  _id: Id<"stations">;
  locationId: Id<"locations">;
  name: string;
  color: string;
  hasScreen: boolean;
  isDefault?: boolean;
  routingMode: "all" | "categories" | "items";
  categoryIds?: string[];
  itemIds?: string[];
  printerIds?: string[];
  printSettings?: {
    autoPrint: "off" | "onReceived" | "onBump";
    printType: "ticket" | "label";
    printOrderTypes: string[];
  };
  displayOptions?: DisplayOptionsState;
  audioSettings?: AudioSettingsState;
  timingThresholds?: TimingThresholdsState;
  modifierKeywords?: ModifierKeywordsState;
  crossScreenMode?:
    | "multi-clear"
    | "send-to-next"
    | "prep-expo"
    | "independent";
  nextStationId?: string | null;
  stationRole?: "prep" | "expo" | null;
  sortMode?: "fifo" | "rule-based" | "hybrid";
  createTicketPerSend?: boolean;
  pairedDevices: Array<{
    _id: string;
    name: string;
    active: boolean;
    hasActiveSession: boolean;
  }>;
}

export interface DisplayOptionsState {
  showOrderNumber: boolean;
  showCustomerName: boolean;
  showServerName: boolean;
  showOrderType: boolean;
  showSeatNumbers: boolean;
  showCourseHeaders: boolean;
  showModifierPrices: boolean;
  showItemCount: boolean;
  hideMetrics: boolean;
  showOrderTypeFilter: boolean;
  fontSize: "small" | "medium" | "large";
}

export interface AudioSettingsState {
  newOrderSound: boolean;
  prioritySound: boolean;
  customerArrivedSound: boolean;
  volume: number;
  newOrderSoundType:
    | "chime"
    | "bell"
    | "alert"
    | "ding"
    | "notification"
    | "none";
  prioritySoundType:
    | "chime"
    | "bell"
    | "alert"
    | "ding"
    | "notification"
    | "none";
  customerArrivedSoundType:
    | "chime"
    | "bell"
    | "alert"
    | "ding"
    | "notification"
    | "none";
}

export interface TimingThresholdsState {
  cautionSeconds: number;
  lateSeconds: number;
}

export interface ModifierKeywordsState {
  allergyKeywords: string[];
  removeKeywords: string[];
  addKeywords: string[];
}

export interface OtherStation {
  _id: string;
  name: string;
  crossScreenMode?:
    | "multi-clear"
    | "send-to-next"
    | "prep-expo"
    | "independent"
    | null;
  stationRole?: "prep" | "expo" | null;
}

export type SoundType =
  | "chime"
  | "bell"
  | "alert"
  | "ding"
  | "notification"
  | "none";

export type CrossScreenMode =
  | "multi-clear"
  | "send-to-next"
  | "prep-expo"
  | "independent";

/** Narrow a stations.getById result to the station edit shape without unsafe casts. */
export function isStationType(value: unknown): value is StationType {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  return (
    "_id" in value &&
    typeof value._id === "string" &&
    "locationId" in value &&
    typeof value.locationId === "string" &&
    "name" in value &&
    typeof value.name === "string" &&
    "color" in value &&
    typeof value.color === "string" &&
    "hasScreen" in value &&
    typeof value.hasScreen === "boolean" &&
    "pairedDevices" in value &&
    Array.isArray(value.pairedDevices)
  );
}

export type RoutingMode = "all" | "categories" | "items";

export type AutoPrint = "off" | "onReceived" | "onBump";

export type PrintType = "ticket" | "label";

export type SortMode = "fifo" | "rule-based" | "hybrid";

export type StationRole = "prep" | "expo" | null;

// Helper function to get routing text
export function getRoutingText(
  routingMode: RoutingMode,
  categoriesCount: number,
  itemsCount: number
): string {
  if (routingMode === "all") {
    return "All Items";
  }
  if (routingMode === "categories") {
    return `${categoriesCount} Categories`;
  }
  return `${itemsCount} Items`;
}

// Helper functions to get default values
export function getDefaultDisplayOptions(
  station: StationType
): DisplayOptionsState {
  return {
    showOrderNumber: station.displayOptions?.showOrderNumber ?? true,
    showCustomerName: station.displayOptions?.showCustomerName ?? true,
    showServerName: station.displayOptions?.showServerName ?? true,
    showOrderType: station.displayOptions?.showOrderType ?? true,
    showSeatNumbers: station.displayOptions?.showSeatNumbers ?? false,
    showCourseHeaders: station.displayOptions?.showCourseHeaders ?? false,
    showModifierPrices: station.displayOptions?.showModifierPrices ?? false,
    showItemCount: station.displayOptions?.showItemCount ?? true,
    hideMetrics: station.displayOptions?.hideMetrics ?? false,
    showOrderTypeFilter: station.displayOptions?.showOrderTypeFilter ?? true,
    fontSize: station.displayOptions?.fontSize ?? "medium",
  };
}

export function getDefaultAudioSettings(
  station: StationType
): AudioSettingsState {
  return {
    newOrderSound: station.audioSettings?.newOrderSound ?? true,
    prioritySound: station.audioSettings?.prioritySound ?? true,
    customerArrivedSound: station.audioSettings?.customerArrivedSound ?? false,
    volume: station.audioSettings?.volume ?? 80,
    newOrderSoundType: station.audioSettings?.newOrderSoundType ?? "chime",
    prioritySoundType: station.audioSettings?.prioritySoundType ?? "bell",
    customerArrivedSoundType:
      station.audioSettings?.customerArrivedSoundType ?? "ding",
  };
}

export function getDefaultTimingThresholds(
  station: StationType
): TimingThresholdsState {
  return {
    cautionSeconds: station.timingThresholds?.cautionSeconds ?? 300,
    lateSeconds: station.timingThresholds?.lateSeconds ?? 600,
  };
}

export function getDefaultModifierKeywords(
  station: StationType
): ModifierKeywordsState {
  return {
    allergyKeywords: station.modifierKeywords?.allergyKeywords ?? [
      "allergy",
      "allergic",
      "no",
    ],
    removeKeywords: station.modifierKeywords?.removeKeywords ?? [
      "no",
      "without",
      "remove",
    ],
    addKeywords: station.modifierKeywords?.addKeywords ?? [
      "extra",
      "add",
      "more",
    ],
  };
}

// Change detection helpers
function checkBasicChanges(
  station: StationType,
  current: {
    name: string;
    color: string;
    hasScreen: boolean;
    routingMode: RoutingMode;
    selectedPrinterIds: string[];
    autoPrint: AutoPrint;
    printType: PrintType;
    printOrderTypes: string[];
    categoriesSelected: string[];
    itemsSelected: string[];
  }
): boolean {
  const defaultPrintOrderTypes = ["dineIn", "takeout", "delivery"];
  const printerIdsChanged =
    JSON.stringify([...current.selectedPrinterIds].toSorted()) !==
    JSON.stringify([...(station.printerIds ?? [])].toSorted());
  const orderTypesChanged =
    JSON.stringify([...current.printOrderTypes].toSorted()) !==
    JSON.stringify(
      [
        ...(station.printSettings?.printOrderTypes ?? defaultPrintOrderTypes),
      ].toSorted()
    );
  const categoriesChanged =
    JSON.stringify(current.categoriesSelected.toSorted()) !==
    JSON.stringify((station.categoryIds ?? []).toSorted());
  const itemsChanged =
    JSON.stringify(current.itemsSelected.toSorted()) !==
    JSON.stringify((station.itemIds ?? []).toSorted());

  return (
    current.name !== station.name ||
    current.color !== station.color ||
    current.hasScreen !== station.hasScreen ||
    current.routingMode !== station.routingMode ||
    printerIdsChanged ||
    current.autoPrint !== (station.printSettings?.autoPrint ?? "off") ||
    current.printType !== (station.printSettings?.printType ?? "ticket") ||
    orderTypesChanged ||
    categoriesChanged ||
    itemsChanged
  );
}

function checkDisplayChanges(
  station: StationType,
  displayOptions: DisplayOptionsState
): boolean {
  return (
    JSON.stringify(displayOptions) !==
    JSON.stringify(getDefaultDisplayOptions(station))
  );
}

function checkAudioChanges(
  station: StationType,
  audioSettings: AudioSettingsState
): boolean {
  return (
    JSON.stringify(audioSettings) !==
    JSON.stringify(getDefaultAudioSettings(station))
  );
}

function checkTimingChanges(
  station: StationType,
  timingThresholds: TimingThresholdsState
): boolean {
  return (
    timingThresholds.cautionSeconds !==
      (station.timingThresholds?.cautionSeconds ?? 300) ||
    timingThresholds.lateSeconds !==
      (station.timingThresholds?.lateSeconds ?? 600)
  );
}

function checkWorkflowChanges(
  station: StationType,
  crossScreenMode: CrossScreenMode,
  nextStationId: string | null,
  stationRole: StationRole
): boolean {
  return (
    crossScreenMode !== (station.crossScreenMode ?? "independent") ||
    nextStationId !== (station.nextStationId ?? null) ||
    stationRole !== (station.stationRole ?? null)
  );
}

function checkAdvancedChanges(
  station: StationType,
  modifierKeywords: ModifierKeywordsState,
  sortMode: SortMode,
  createTicketPerSend: boolean
): boolean {
  return (
    JSON.stringify(modifierKeywords) !==
      JSON.stringify(getDefaultModifierKeywords(station)) ||
    sortMode !== (station.sortMode ?? "fifo") ||
    createTicketPerSend !== (station.createTicketPerSend ?? false)
  );
}

export function hasAnyStationChanges(
  station: StationType,
  state: {
    name: string;
    color: string;
    hasScreen: boolean;
    routingMode: RoutingMode;
    selectedPrinterIds: string[];
    autoPrint: AutoPrint;
    printType: PrintType;
    printOrderTypes: string[];
    categoriesSelected: string[];
    itemsSelected: string[];
    displayOptions: DisplayOptionsState;
    audioSettings: AudioSettingsState;
    timingThresholds: TimingThresholdsState;
    crossScreenMode: CrossScreenMode;
    nextStationId: string | null;
    stationRole: StationRole;
    modifierKeywords: ModifierKeywordsState;
    sortMode: SortMode;
    createTicketPerSend: boolean;
  }
): boolean {
  return (
    checkBasicChanges(station, state) ||
    checkDisplayChanges(station, state.displayOptions) ||
    checkAudioChanges(station, state.audioSettings) ||
    checkTimingChanges(station, state.timingThresholds) ||
    checkWorkflowChanges(
      station,
      state.crossScreenMode,
      state.nextStationId,
      state.stationRole
    ) ||
    checkAdvancedChanges(
      station,
      state.modifierKeywords,
      state.sortMode,
      state.createTicketPerSend
    )
  );
}

// Helper function to compute nextStationId for the update payload
function computeNextStationId(
  hasScreen: boolean,
  crossScreenMode: CrossScreenMode,
  stationRole: StationRole,
  nextStationId: string | null
): Id<"stations"> | null {
  if (!(hasScreen && nextStationId)) {
    return null;
  }
  const shouldIncludeNextStation =
    crossScreenMode === "send-to-next" ||
    (crossScreenMode === "prep-expo" && stationRole === "prep");
  return shouldIncludeNextStation
    ? toConvexTableId("stations", nextStationId)
    : null;
}

// Helper function to compute stationRole for the update payload
function computeStationRole(
  hasScreen: boolean,
  crossScreenMode: CrossScreenMode,
  stationRole: StationRole
): "prep" | "expo" | null {
  if (!hasScreen || crossScreenMode !== "prep-expo" || !stationRole) {
    return null;
  }
  return stationRole;
}

export function buildStationUpdatePayload(opts: {
  station: StationType;
  name: string;
  color: string;
  hasScreen: boolean;
  routingMode: RoutingMode;
  selectedPrinterIds: string[];
  autoPrint: AutoPrint;
  printType: PrintType;
  printOrderTypes: string[];
  categoriesSelected: string[];
  itemsSelected: string[];
  displayOptions: DisplayOptionsState;
  audioSettings: AudioSettingsState;
  timingThresholds: TimingThresholdsState;
  crossScreenMode: CrossScreenMode;
  modifierKeywords: ModifierKeywordsState;
  sortMode: SortMode;
  createTicketPerSend: boolean;
  nextStationId: string | null;
  stationRole: StationRole;
}) {
  const printSettings =
    opts.selectedPrinterIds.length > 0
      ? {
          autoPrint: opts.autoPrint,
          printType: opts.printType,
          printOrderTypes: opts.printOrderTypes,
        }
      : undefined;

  const categoryIds =
    opts.routingMode === "categories"
      ? opts.categoriesSelected.map((id) => toConvexTableId("categories", id))
      : [];
  const itemIds =
    opts.routingMode === "items"
      ? opts.itemsSelected.map((id) => toConvexTableId("items", id))
      : [];

  const kdsSettings = opts.hasScreen
    ? {
        displayOptions: opts.displayOptions,
        audioSettings: opts.audioSettings,
        timingThresholds: opts.timingThresholds,
        crossScreenMode: opts.crossScreenMode,
        modifierKeywords: opts.modifierKeywords,
        sortMode: opts.sortMode,
        createTicketPerSend: opts.createTicketPerSend,
      }
    : {
        displayOptions: undefined,
        audioSettings: undefined,
        timingThresholds: undefined,
        crossScreenMode: undefined,
        modifierKeywords: undefined,
        sortMode: undefined,
        createTicketPerSend: undefined,
      };

  return {
    id: opts.station._id,
    name: opts.name.trim(),
    color: opts.color,
    hasScreen: opts.hasScreen,
    routingMode: opts.routingMode,
    printerIds: opts.selectedPrinterIds.map((id) =>
      toConvexTableId("printers", id)
    ),
    printSettings,
    categoryIds,
    itemIds,
    ...kdsSettings,
    nextStationId: computeNextStationId(
      opts.hasScreen,
      opts.crossScreenMode,
      opts.stationRole,
      opts.nextStationId
    ),
    stationRole: computeStationRole(
      opts.hasScreen,
      opts.crossScreenMode,
      opts.stationRole
    ),
  };
}

// Helper to determine station status
export function getStationStatus(station: StationType) {
  const hasActiveScreen = station.hasScreen && station.pairedDevices.length > 0;
  const hasPrinter = !!station.printerIds?.length;
  if (hasActiveScreen && hasPrinter) {
    return "full";
  }
  if (hasActiveScreen || hasPrinter) {
    return "partial";
  }
  return "inactive";
}

export function getStatusBadge(status: "full" | "partial" | "inactive") {
  switch (status) {
    case "full":
      return {
        variant: "success" as const,
        label: "Active",
        dotClass: "bg-success",
      };
    case "partial":
      return {
        variant: "warning" as const,
        label: "Partial",
        dotClass: "bg-warning",
      };
    default:
      return {
        variant: "destructive" as const,
        label: "Inactive",
        dotClass: "bg-destructive/50",
      };
  }
}

// Helper function to get station role description
export function getStationRoleDescription(
  stationRole: "prep" | "expo" | null
): string {
  if (stationRole === "prep") {
    return "Prep stations work on their filtered items in parallel";
  }
  if (stationRole === "expo") {
    return "Expo station receives the full order when all prep is done";
  }
  return "Select a role for this station";
}
